import { useState } from 'react';
import { fiat, ApiClientError, type BankInfo } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, CopyButton, Empty, Modal, Spinner, Stat } from '../components/ui';

function payoutTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'danger';
  return 'warning';
}

export function FiatAccount() {
  const info = useResource(
    () => fiat.get().catch((e) => (e instanceof ApiClientError && e.status === 404 ? null : Promise.reject(e))),
    [],
    { refreshMs: 25000 }
  );
  const collections = useResource(() => fiat.collections(1).catch(() => null), [], { refreshMs: 25000 });
  const payouts = useResource(() => fiat.payouts(1).catch(() => null), [], { refreshMs: 25000 });
  const banks = useResource<BankInfo[]>(() => fiat.banks().catch(() => []), []);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState('');
  const [account, setAccount] = useState('');
  const [bankUuid, setBankUuid] = useState('');
  const [resolvedName, setResolvedName] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>, after?: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      if (after) await after();
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  }

  async function resolveName() {
    if (!/^\d{10}$/.test(account) || !bankUuid) return;
    setResolvedName(null);
    try {
      const result = await fiat.resolve(account, bankUuid);
      setResolvedName(result.account_name);
    } catch {
      setResolvedName(null);
    }
  }

  if (info.loading) return <Spinner />;

  if (!info.data) {
    return (
      <>
        <div className="page-header">
          <h1>Fiat account</h1>
          <p>Your NGN account — collections in, payouts out.</p>
        </div>
        {error && <Alert>{error}</Alert>}
        <Card>
          <Empty>
            <p>No fiat account yet.</p>
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => run(() => fiat.create(), () => info.reload())}
            >
              {busy ? 'Creating…' : 'Create NGN account'}
            </button>
          </Empty>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1>Fiat account</h1>
        <p>Bank transfers into this account fund your NGN balance.</p>
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="stat-grid">
        <Stat label="Balance (NGN)" value={`₦${info.data.balance}`} />
        <Stat label="Float" value={`${info.data.account_name} · ${info.data.account_number}`} />
      </div>

      {info.data.deposit_account && (
        <Card title="Fund your balance">
          <div className="card-body" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <div className="stat-label">Account number</div>
              <div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>
                {info.data.deposit_account.account_number}{' '}
                <CopyButton value={info.data.deposit_account.account_number} />
              </div>
            </div>
            <div>
              <div className="stat-label">Bank</div>
              <div>{info.data.deposit_account.bank_name}</div>
            </div>
            <div>
              <div className="stat-label">Account name</div>
              <div>{info.data.deposit_account.account_name}</div>
            </div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Transfer from any Nigerian bank — credits land automatically.
            </div>
          </div>
        </Card>
      )}

      <div style={{ height: 16 }} />

      <div style={{ display: 'grid', gap: 16 }}>
        <Card
          title="Payouts"
          actions={
            <button className="btn btn-primary btn-sm" onClick={() => setPaying(true)}>
              New payout
            </button>
          }
        >
          {!payouts.data?.items.length ? (
            <Empty>No payouts yet.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Amount</th>
                    <th>Destination</th>
                    <th>Recipient</th>
                    <th>Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.data.items.map((payout) => (
                    <tr key={payout.id}>
                      <td className="muted">{formatDateTime(payout.created_at)}</td>
                      <td className="mono">₦{payout.amount}</td>
                      <td className="mono">{payout.destination_account}</td>
                      <td>{payout.recipient_name ?? '—'}</td>
                      <td>{payout.offramp_order_id ? <Badge tone="accent">offramp</Badge> : 'manual'}</td>
                      <td>
                        <Badge tone={payoutTone(payout.status)}>{payout.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Collections">
          {!collections.data?.items.length ? (
            <Empty>Transfers into your account number show up here.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Amount</th>
                    <th>Payer</th>
                    <th>Bank</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.data.items.map((collection) => (
                    <tr key={collection.id}>
                      <td className="muted">{formatDateTime(collection.created_at)}</td>
                      <td className="mono">₦{collection.amount}</td>
                      <td>{collection.payer_name ?? '—'}</td>
                      <td>{collection.payer_bank_name ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {paying && (
        <Modal
          title="New payout"
          onClose={() => setPaying(false)}
          footer={
            <>
              <button className="btn" onClick={() => setPaying(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={busy || !amount || !account || !bankUuid}
                onClick={() =>
                  run(
                    () =>
                      fiat.payout({
                        amount,
                        destination_account: account,
                        destination_bank_uuid: bankUuid,
                      }),
                    async () => {
                      setPaying(false);
                      setAmount('');
                      setAccount('');
                      setResolvedName(null);
                      await Promise.all([info.reload(), payouts.reload()]);
                    }
                  )
                }
              >
                {busy ? 'Sending…' : 'Send payout'}
              </button>
            </>
          }
        >
          <label className="field">
            <span>Bank</span>
            <select value={bankUuid} onChange={(event) => setBankUuid(event.target.value)}>
              <option value="">Select a bank…</option>
              {(banks.data ?? []).map((bank) => (
                <option key={bank.uuid} value={bank.uuid}>
                  {bank.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Account number</span>
            <input
              value={account}
              onChange={(event) => setAccount(event.target.value)}
              onBlur={resolveName}
              maxLength={10}
              className="mono"
            />
            {resolvedName && (
              <span className="muted" style={{ fontSize: 12.5 }}>
                {resolvedName}
              </span>
            )}
          </label>
          <label className="field">
            <span>Amount (NGN)</span>
            <input value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="50000.00" />
          </label>
        </Modal>
      )}
    </>
  );
}
